import { AuthFilterAll } from "./auth-filter";
import { UserClaimType, UserClaims } from "../types/users";

// Compares 2 sets of claims and returns true if different
export function diffClaims(claims1: UserClaims | UserClaimType | undefined, claims2: UserClaims | UserClaimType | undefined): boolean {
  if (!claims1 && !claims2) return false;
  if (!claims1 || !claims2) return true;

  const json1 = serialiseClaims(claims1);
  const json2 = serialiseClaims(claims2);

  return json1 !== json2;
}

export function removeDups(arr: string[]): string[] {
  return arr.reduce((acc: string[], curr: string) => {
    if (!acc.includes(curr)) acc.push(curr);
    return acc;
  }, []);
}

export function hasFilterAll(arr: string[]): boolean {
  return arr.some((a) => a === AuthFilterAll);
}

// Reduces a claim to the minimum set;
//  1. "*" means we can remove all other entries
//  2. Remove dups
//  3. If there is only 1 entry, return a string not an array
export function flattenClaim(claim: string[] | undefined): UserClaimType | undefined {
  if (!claim) return undefined;

  if (hasFilterAll(claim)) {
    return AuthFilterAll;
  }

  const unique = removeDups(claim);

  // If there's only 1 entry - return it direct
  if (unique.length === 1) return unique[0];

  return unique;
}

// Merges a single claim
export function mergeClaimType(claim1: UserClaimType | undefined, claim2: UserClaimType | undefined): { merged: UserClaimType | undefined; diff: boolean } {
  if (!claim1 && !claim2) return { merged: undefined, diff: false };
  if (!claim2) return { merged: claim1, diff: false };
  if (!claim1) return { merged: claim2, diff: true };

  let merged: string[] = [];

  // If both are strings
  if (typeof claim1 === "string" && typeof claim2 === "string") {
    merged = [claim1, claim2];
  } else if (typeof claim1 === "string") {
    merged = [claim1, ...claim2];
  } else if (typeof claim2 === "string") {
    merged = [...claim1, claim2];
  }

  // Then we flatten the array
  const final = flattenClaim(merged);

  // Then work out if there's a diff
  const diff = diffClaims(claim1, final);

  return { merged: final, diff: diff };
}

// Merges in a set of claims and returns if there were previous differences
export function mergeClaims(claims1: UserClaims | undefined, claims2: UserClaims | undefined): { merged: UserClaims; diff: boolean } {
  let diff = false;
  const merged = { ...claims1 };

  for (const prop in claims2) {
    const mergeClaim = claims2[prop];
    const existing = merged[prop];

    if (!existing) {
      (merged as any)[prop] = mergeClaim;
      diff = true;
    } else if (diffClaims(existing, mergeClaim)) {
      //console.log("Diff", prop, existing, mergeClaim);

      const result = mergeClaimType(existing, mergeClaim);
      (merged as any)[prop] = result.merged;
      diff = result.diff;
    }
  }

  return { merged: merged, diff: diff };
}

// Convert our array of claims to a single object
// To: { say_hello: '*', viewer: '*' }
export const convertClaimArrayToClaims = (claims: UserClaims[]): UserClaims => {
  const claimObj = claims.reduce(function (acc: UserClaims, cur: UserClaims, i: number): UserClaims {
    const updated = mergeClaims(acc, cur).merged;
    return updated;
  }, {});

  return claimObj;
};

export function serialiseClaims(claims: UserClaims | UserClaimType): string {
  return JSON.stringify(claims);
}
